import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Modal from "react-modal";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { AuthGuard, EmailGuard } from "../App";
import { Oval } from "react-loader-spinner";
import { FaRecycle, FaHome, FaSearch, FaPlus, FaPen, FaTimes, FaArrowLeft, FaDollarSign } from 'react-icons/fa';
import { StandardURL } from "../App";

// Set app element for the modal for accessibility
Modal.setAppElement('#root'); // Assuming your root element has id="root"

const TrashUnitsPage = () => {
    const [trashFee, setTrashFee] = useState(null); // Use null initially to distinguish between no search and no data
    const [showAddModal, setShowAddModal] = useState(false);
    const [amount, setAmount] = useState(""); // Initialize as empty string
    const [propertyId, setPropertyId] = useState("");
    const [isLoading, setIsLoading] = useState(false); // For API call loading state
    const [isFetching, setIsFetching] = useState(false); // For fetch-specific loading
    const navigate = useNavigate();

    // Debounced fetch on propertyId change
    useEffect(() => {
        // Debounce logic (waits 500ms after user stops typing)
        const delaySearch = setTimeout(() => {
            if (propertyId) {
                fetchTrashFee(propertyId);
            } else {
                setTrashFee(null); // Clear data if input is cleared
            }
        }, 500);

        return () => clearTimeout(delaySearch);
    }, [propertyId]); 

    const fetchTrashFee = async (id) => {
        const token = localStorage.getItem("token");
        if (!id) return;
        
        setIsFetching(true);
        setTrashFee(null); // Clear previous results

        try {
            const response = await axios.get(
                `${StandardURL}/iNyumba/trashfee/fetchonetrashfee/${id}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            
            if (response.data.response && response.data.response.feeamount !== undefined) {
                setTrashFee(response.data.response);
                toast.success("Trash fee data fetched successfully!");
            } else {
                setTrashFee({}); // Use an empty object to signify 'no existing fee'
                toast.info(`No existing trash fee found for Property ID: ${id}`);
            }

        } catch (error) {
            console.error("Error fetching trash fee:", error);
            setTrashFee({}); // Treat API error or not-found as 'no existing fee' to allow adding
            toast.error(
                error.response?.data?.response || "Failed to fetch trash fee data. Check ID."
            );
        } finally {
            setIsFetching(false);
        }
    };

    const handleAddTrashFee = () => {
        if (!propertyId || !amount || isNaN(parseFloat(amount)) || parseFloat(amount) <= 0) {
            toast.error("Please enter a valid Property ID and a positive fee amount.");
            return;
        }

        setIsLoading(true);
        const token = localStorage.getItem("token");
        const formData = new FormData();
        formData.append("feeamount", amount);

        axios
            .post(
                `${StandardURL}/iNyumba/trashfee/posttrashfee/${propertyId}`,
                formData,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            )
            .then((response) => {
                toast.success(response.data.response || "Trash fee added successfully!");
                setShowAddModal(false);
                setAmount(""); // Clear modal input
                fetchTrashFee(propertyId); // Re-fetch the data to update the display
            })
            .catch((error) => {
                toast.error(error.response?.data?.response || "Failed to add trash fee. It might already exist.");
                console.error("Error adding trash fee:", error);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const handleUpdateTrashFee = () => {
        // Only navigate if a fee was successfully fetched
        if (trashFee?.feeamount !== undefined) {
            // Store the property ID in local storage for the update page
            localStorage.setItem("propertyIdTrash", JSON.stringify(propertyId));
            navigate("/admin/trashunitsupdate"); // Navigate to trash fee update page
        } else {
            toast.warn("No existing trash fee to update. Please add one first.");
        }
    };

    const handleBack = () => {
        navigate(-1); // Go back to the previous page
    };

    // Helper to format currency
    const formatCurrency = (amount) => {
        const value = parseFloat(amount);
        return isNaN(value) ? 'N/A' : `Ksh ${value.toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    return (
        <EmailGuard>
            <AuthGuard>
                <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8">
                    
                    <div className="max-w-3xl mx-auto">
                        {/* Header and Back Button */}
                        <div className="flex items-center justify-between mb-8">
                            <h1 className="text-3xl font-extrabold text-gray-900 flex items-center">
                                <FaRecycle className="mr-3 text-indigo-600" /> Manage Trash Fee
                            </h1>
                            <button
                                onClick={handleBack}
                                className="inline-flex items-center bg-gray-600 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded-lg transition duration-150 shadow-md"
                            >
                                <FaArrowLeft className="mr-2" /> Back
                            </button>
                        </div>

                        {/* Search Card */}
                        <div className="bg-white shadow-xl rounded-xl p-6 mb-8 border-t-4 border-indigo-500">
                            <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                                <FaSearch className="mr-2 text-indigo-500" /> Lookup Property
                            </h2>
                            <label htmlFor="propertyId" className="block text-sm font-medium text-gray-700 mb-2">
                                Enter Property ID:
                            </label>
                            <input
                                type="text"
                                id="propertyId"
                                className="w-full border border-gray-300 rounded-lg py-2.5 px-4 focus:ring-indigo-500 focus:border-indigo-500 transition"
                                value={propertyId}
                                onChange={(e) => setPropertyId(e.target.value)}
                                placeholder="Start typing property ID to search..."
                            />
                            {isFetching && (
                                <div className="mt-3 flex items-center text-sm text-indigo-600">
                                    <Oval height={20} width={20} color="#4f46e5" visible={true} ariaLabel="loading-indicator" secondaryColor="#6366f1" />
                                    <span className="ml-2">Searching...</span>
                                </div>
                            )}
                        </div>

                        {/* Trash Fee Details Card (Conditional Display) */}
                        <div className="bg-white shadow-xl rounded-xl p-6">
                            <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center border-b pb-2">
                                <FaDollarSign className="mr-2 text-green-600" /> Fee Status for Property ID: {propertyId || '...'}
                            </h2>

                            {propertyId && !isFetching && (
                                <div className="space-y-4">
                                    {/* Fee Display */}
                                    <div className="bg-gray-100 p-4 rounded-lg flex items-center justify-between">
                                        <div className="flex items-center">
                                            <FaHome className="text-3xl text-gray-500 mr-4" />
                                            <div>
                                                <p className="text-sm font-medium text-gray-600">Current Trash Fee Amount:</p>
                                                <p className="text-2xl font-extrabold text-indigo-600">
                                                    {trashFee && trashFee.feeamount !== undefined ? formatCurrency(trashFee.feeamount) : 'N/A'}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    {/* Action Buttons */}
                                    <div className="flex flex-col sm:flex-row gap-3 pt-3">
                                        {trashFee && trashFee.feeamount !== undefined ? (
                                            <button
                                                onClick={handleUpdateTrashFee}
                                                className="w-full sm:w-auto inline-flex items-center justify-center bg-green-600 hover:bg-green-700 text-white font-bold py-2.5 px-6 rounded-lg transition duration-150 shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                                                disabled={isLoading}
                                            >
                                                <FaPen className="mr-2" /> Update Fee
                                            </button>
                                        ) : (
                                            <button
                                                onClick={() => {
                                                    setAmount(""); // Clear input when opening modal
                                                    setShowAddModal(true);
                                                }}
                                                className="w-full sm:w-auto inline-flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white font-bold py-2.5 px-6 rounded-lg transition duration-150 shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                                disabled={isLoading}
                                            >
                                                <FaPlus className="mr-2" /> Add Fee
                                            </button>
                                        )}
                                    </div>
                                    <p className="text-sm text-red-500 italic mt-4">
                                        Note: Do not add a duplicate entry. Use "Update Fee" if an amount is already displayed.
                                    </p>
                                </div>
                            )}

                            {/* Initial/Empty state messages */}
                            {!propertyId && !isFetching && (
                                <p className="text-center text-gray-500 py-8">
                                    Please enter a **Property ID** above to check for an existing trash fee.
                                </p>
                            )}
                            
                            {/* Loading State for Initial Fetch */}
                            {isFetching && (
                                <p className="text-center text-gray-500 py-8">Fetching trash fee details...</p>
                            )}

                        </div>
                    </div>
                </div>

                {/* Modal for Adding Trash Fee */}
                <Modal
                    isOpen={showAddModal}
                    onRequestClose={() => setShowAddModal(false)}
                    contentLabel="Add Trash Fee Modal"
                    className="modal fixed inset-0 flex items-center justify-center p-4 z-50"
                    overlayClassName="overlay fixed inset-0 bg-gray-900 bg-opacity-75"
                >
                    <div className="bg-white rounded-xl shadow-2xl p-6 max-w-sm w-full transform transition-all">
                        <div className="flex justify-between items-center border-b pb-3 mb-4">
                            <h2 className="text-2xl font-bold text-gray-800 flex items-center">
                                <FaPlus className="mr-2 text-indigo-600"/> Add Trash Fee
                            </h2>
                            <button onClick={() => setShowAddModal(false)} className="text-gray-400 hover:text-gray-600 transition">
                                <FaTimes className="w-5 h-5" />
                            </button>
                        </div>

                        <p className="text-sm text-red-600 mb-4">
                            **WARNING:** Only use this to set the **initial** trash fee for **Property ID: {propertyId}**. Use the update button after initial setup.
                        </p>

                        <div className="mb-4">
                            <label
                                htmlFor="amount"
                                className="block text-sm font-medium text-gray-700 mb-1"
                            >
                                <FaDollarSign className="inline mr-1"/> Fee Amount (KES):
                            </label>
                            <input
                                type="number"
                                id="amount"
                                className="w-full border border-gray-300 rounded-lg py-2.5 px-4 focus:ring-indigo-500 focus:border-indigo-500 transition"
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                placeholder="Enter amount (e.g., 500.00)"
                                min="0.01"
                                step="any"
                            />
                        </div>
                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={() => setShowAddModal(false)}
                                className="bg-gray-200 text-gray-700 font-bold py-2 px-4 rounded-lg hover:bg-gray-300 transition duration-150"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleAddTrashFee}
                                disabled={!amount || isLoading || parseFloat(amount) <= 0}
                                className={`inline-flex items-center justify-center font-bold py-2 px-4 rounded-lg transition duration-150 shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                                    isLoading
                                        ? "bg-indigo-400 text-white cursor-not-allowed"
                                        : "bg-indigo-600 hover:bg-indigo-700 text-white focus:ring-indigo-500"
                                }`}
                            >
                                {isLoading ? (
                                    <Oval height={20} width={20} color="white" visible={true} ariaLabel="loading-indicator" secondaryColor="#a5b4fc" />
                                ) : (
                                    "Confirm Add"
                                )}
                            </button>
                        </div>
                    </div>
                </Modal>
            </AuthGuard>
        </EmailGuard>
    );
};

export default TrashUnitsPage;